import { Divider, Group, Select } from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useState } from 'react';
import type { Task } from '../../../api/tasks-client/task-client.type';
import { useTaskSearch } from '../../../api/tasks-client/tasks-client.hooks';
import i18n from '../../../base-dictionary';
import { useProcessStore } from '../../../store/process-store/process-store.hooks';
import { secondaryBase } from '../../../styles/design-tokens';
import { Icon } from '../../../ui-library/icon/icon';
import { Tooltip } from '../../../ui-library/tooltip/tooltip';
import { Text } from '../../../ui-library/typography/typography';

type TaskSelectProps = {
  hideLabel?: boolean;
  handleTaskSelected?: (task: Task | null) => void;
  isRightSide?: boolean;
  startDate?: string | null;
  endDate?: string | null;
};

type TaskSelectRef = {
  clearSelection: () => void;
};

export const TaskSelect = forwardRef<TaskSelectRef, TaskSelectProps>(
  ({ hideLabel, handleTaskSelected, isRightSide = false, startDate, endDate }, ref) => {
    const { scope: leftScope, setScope: setLeftScope, rightScope, setRightScope } = useProcessStore();

    // Create completely isolated scope references
    const scope = useMemo(() => {
      return isRightSide ? rightScope : leftScope;
    }, [isRightSide, rightScope, leftScope]);

    const { team, board, workflow, task, taskType } = scope;

    // Completely isolated local state
    const [searchValue, setSearchValue] = useState('');
    const [debouncedSearch] = useDebouncedValue(searchValue, 300);

    // Initialize search value based on current selected task
    useEffect(() => {
      if (task) {
        setSearchValue('');
      }
    }, [task]);

    const { tasks } = useTaskSearch(debouncedSearch, team?.id, board?.id, workflow?.id, taskType, startDate, endDate, {
      enabled: Boolean(debouncedSearch && (team?.id || board?.id || workflow?.id)),
      queryKey: [
        'tasks',
        debouncedSearch,
        team?.id,
        board?.id,
        workflow?.id,
        taskType,
        startDate,
        endDate,
        isRightSide ? 'right' : 'left',
      ],
    });

    const handleChange = useCallback(
      (value: string) => {
        // Find the selected task
        const selectedTask = tasks.find((t: Task) => t.id === value);
        if (selectedTask) {
          // User selected a task from the dropdown - be extremely explicit
          if (isRightSide) {
            setRightScope({
              task: selectedTask,
            });
          } else {
            setLeftScope({
              task: selectedTask,
            });
          }
          handleTaskSelected?.(selectedTask);
        }
      },
      [tasks, handleTaskSelected, isRightSide, setLeftScope, setRightScope],
    );

    const handleSearchChange = useCallback((value: string) => {
      setSearchValue(value);
    }, []);

    const clearSelection = useCallback(() => {
      setSearchValue('');

      if (isRightSide) {
        setRightScope({
          task: null,
        });
      } else {
        setLeftScope({
          task: null,
        });
      }
      handleTaskSelected?.(null);
    }, [handleTaskSelected, isRightSide, setLeftScope, setRightScope]);

    useImperativeHandle(ref, () => ({ clearSelection }), [clearSelection]);

    // Create task options and ensure the selected task is always included
    const taskOptions = useMemo(() => {
      let options = tasks.map((taskItem) => ({
        value: taskItem.id,
        label: `${taskItem.url ? taskItem.url.split('/').pop() : ''} - ${taskItem.name}`,
      }));

      // If there's a selected task but it's not in the current search results, add it
      if (task && !options.some((option) => option.value === task.id)) {
        options = [
          {
            value: task.id,
            label: `${task.url ? task.url.split('/').pop() : ''} - ${task.name}`,
          },
          ...options,
        ];
      }

      return options;
    }, [tasks, task]);

    const taskString = `${i18n.t('common.work_unit')}`;

    return (
      <div style={{ marginTop: '30px' }}>
        <Divider style={{ marginBottom: '15px' }} />
        {!hideLabel && (
          <Group style={{ marginBottom: '15px' }} gap={5}>
            <Text size="small" weight="medium">
              {taskString}
            </Text>
            <Tooltip
              label={`Search for a ${taskString}. Type in the ${taskString.toLocaleLowerCase()} name or URL. Limit of 10 results based on the search query.`}
              variant="black"
              multiline
              maw={300}
            >
              <Icon name="info" size={16} color={secondaryBase} />
            </Tooltip>
          </Group>
        )}
        <Select
          placeholder={`Type to search for a ${taskString}`}
          data={taskOptions}
          value={task ? task.id : null}
          onChange={(value) => handleChange(value as string)}
          onSearchChange={handleSearchChange}
          onClear={clearSelection}
          searchable
          clearable
          allowDeselect
          radius="xl"
          comboboxProps={{ position: 'top' }}
        />
        <Divider style={{ marginTop: '30px' }} />
      </div>
    );
  },
);
